import React, { ComponentProps, forwardRef, HTMLAttributes, ReactNode } from 'react'
import { NavLink, useMatch, useResolvedPath } from 'react-router'
import { Box, BoxProps, Stack } from '@ui'
import { clsx } from 'clsx'
import { useAppTransition } from 'contexts/AppTransitionContext'

import {
    navItemBackgroundStyle,
    navItemLinkSelectedStyle,
    navItemLinkStyle,
    navItemSpaceLinkStyle,
    squareNavItemHoverAreaInsideStyle,
    squareNavItemHoverAreaStyle,
} from './_NavItem.css'

type NavLinkProps = {
    to?: string
    exact?: boolean
    active?: boolean
    forceMatch?: boolean
    minHeight?: BoxProps['minHeight']
}

export const NavItem = forwardRef<
    HTMLElement,
    { id?: string; highlight?: boolean; activeBackground?: BoxProps['background'] } & NavLinkProps &
        BoxProps &
        HTMLAttributes<HTMLDivElement>
>(
    (
        {
            id,
            to,
            exact,
            highlight: isHighlight,
            activeBackground,
            forceMatch,
            children,
            minHeight,
            ...props
        },
        ref,
    ) => {
        const resolved = useResolvedPath(`/${to === '/' ? '' : to}`)

        const match =
            useMatch({
                path: resolved.pathname || '/',
                end: to === '/' || exact,
            }) || forceMatch
        const selected = !!match

        return (
            <ConditionalNavLink to={to} className={navItemLinkStyle}>
                <Box pointerEvents="all">
                    <Stack position="relative" paddingX="sm" paddingY="xs" {...props} ref={ref}>
                        <Stack
                            horizontal
                            grow
                            background={selected ? (activeBackground ?? 'level2') : undefined}
                            position="relative"
                            rounded="xs"
                            alignItems="center"
                            gap="sm"
                            minHeight={minHeight ?? 'x6'}
                            minWidth="x6"
                            paddingX="sm"
                            color={isHighlight || match ? undefined : 'gray2'}
                            className={navItemBackgroundStyle}
                        >
                            {children}
                        </Stack>
                    </Stack>
                </Box>
            </ConditionalNavLink>
        )
    },
)

const tooltipOptions = {
    placement: 'horizontal',
    immediate: true,
} as const

export const MainSideBarNavItem = forwardRef<
    HTMLElement,
    {
        id?: string
        children: ReactNode
        to?: string
        tooltip?: string
        'aria-label'?: string
        onClick?: (e: React.MouseEvent) => void
        selected?: boolean
        isSpace?: boolean
    }
>((props, ref) => {
    const { id, to, children, onClick, selected, isSpace, tooltip, 'aria-label': ariaLabel } = props

    const linkSelected =
        useMatch({
            path: to ?? '',
        }) || selected

    const parentClassName = clsx({
        [navItemSpaceLinkStyle]: isSpace,
        [navItemLinkStyle]: true,
        [navItemLinkSelectedStyle]: linkSelected,
    })

    return (
        <Box className={parentClassName}>
            <ConditionalNavLink to={to}>
                <Box
                    centerContent
                    id={id}
                    pointerEvents="all"
                    padding="sm"
                    height={isSpace ? 'x7' : 'x6'}
                    userSelect="none"
                    cursor="pointer"
                    aria-label={ariaLabel ?? tooltip}
                    onClick={onClick}
                >
                    <Box padding="xs" className={squareNavItemHoverAreaStyle}>
                        <Box centerContent className={squareNavItemHoverAreaInsideStyle}>
                            <Stack
                                centerContent
                                position="relative"
                                tooltip={tooltip}
                                tooltipOptions={tooltipOptions}
                                ref={ref}
                            >
                                <Stack position="relative">{children}</Stack>
                            </Stack>
                        </Box>
                    </Box>
                </Box>
            </ConditionalNavLink>
        </Box>
    )
})

/**
 * allows `to` prop to be undefined returning children
 **/
export const ConditionalNavLink = ({
    children,
    to,
    ...props
}: Omit<ComponentProps<typeof NavLink>, 'to' | 'children'> & {
    to?: string
    children: ReactNode
}) => {
    const { startTransition } = useAppTransition()
    return !to ? (
        children
    ) : (
        <NavLink
            to={to ? to : ''}
            {...props}
            onClick={(e) => {
                startTransition(() => {
                    performance.mark('towns:nav-item-click')
                    props.onClick?.(e)
                })
            }}
        >
            {children}
        </NavLink>
    )
}
